import React, {useState} from 'react';

import {observer} from 'mobx-react';
import {MenuItem, Select, Tooltip} from '@mui/material';

import {useStores} from '../../../Hooks';
import {trainingAndFieldRoleEnum} from '../../../serverClientShared/sharedEnums';
import DefaultSettingsSection from './DefaultSettingsSection';
import RoleAccessDialog from './RoleAccessDialog';

const ROLES = Object.values(trainingAndFieldRoleEnum).filter((r) => r !== trainingAndFieldRoleEnum.NONE);

const labelClass = 'block mb-1 text-[11px] font-semibold text-main-normal tracking-[0.5px]';
const descClass = 'text-xs text-main-hover mb-2';
const selectSx = {
    fontSize: 13, fontFamily: 'inherit', borderRadius: 2,
    '& fieldset': {borderColor: 'var(--panels-normal-color)'},
    '& .MuiSelect-select': {fontSize: 13, fontFamily: 'inherit', py: 0.75},
};
const paperClass = 'py-2 mt-1 rounded-input shadow-lg';
const paperSx = {fontFamily: 'inherit', '& *': {fontFamily: 'inherit'}};
const MENU_PROPS = {PaperProps: {className: paperClass, sx: paperSx}};
const countBadgeStyle = {backgroundColor: 'color-mix(in srgb, var(--classicbutton-normal-color) 10%, transparent)'};
const menuItemClass = '!flex !items-center !px-3 !py-2 !mb-0 !rounded-none aria-selected:!bg-panels-normal hover:aria-selected:!bg-panels-hover';
const menuItemSx = {
    fontFamily: 'inherit',
    borderBottom: '1px solid var(--panels-normal-color)',
    '&:last-child': {borderBottom: 'none'},
};

const RoleAccessRulesSection = observer(({roleAccess, isRolesPermittedByDefault, onChangeDefault}) => {
    const {generalStore} = useStores();
    const [dialogRole, setDialogRole] = useState(null);
    const {getRoleCount, changes, updateRoleChanges, projectId, projectName, workspace, summary} = roleAccess;

    const handleSaveDialog = (diffs) => {
        updateRoleChanges(dialogRole, diffs);
        setDialogRole(null);
    };

    return (
        <div>
            <span className={labelClass}>ROLE ACCESS RULES</span>
            <p className={descClass}>
                {generalStore.translate('configureWhichRolesCanAccessContent') || 'Configure which roles can access folders and items in this project'}
            </p>
            <Select
                value=""
                onChange={(e) => setDialogRole(e.target.value)}
                displayEmpty
                size="small"
                fullWidth
                sx={selectSx}
                MenuProps={MENU_PROPS}
                renderValue={() => generalStore.translate('manageRolesAccess') || 'Manage roles access'}
            >
                {ROLES.map((role) => {
                    const {permitted, total} = getRoleCount(role);
                    return (
                        <MenuItem key={role} value={role} className={menuItemClass} sx={menuItemSx}>
                            <div className="flex items-center justify-between w-full">
                                <span className="text-[13px] font-semibold text-main-normal">{generalStore.translateRole(role)}</span>
                                <Tooltip
                                    title={`${permitted} of ${total} items are accessible by this role`}
                                    placement="top"
                                    enterDelay={300}
                                    componentsProps={{tooltip: {className: '!text-base !text-main-normal !bg-background-normal !border !border-panels-normal !shadow-lg !rounded-input'}}}
                                >
                                    <span
                                        className="inline-flex items-center px-2 py-0.5 rounded-menu text-xs font-medium text-classic-button-normal cursor-default"
                                        style={countBadgeStyle}
                                    >
                                        {permitted}/{total}
                                    </span>
                                </Tooltip>
                            </div>
                        </MenuItem>
                    );
                })}
            </Select>
            <div className="mt-3">
                <DefaultSettingsSection
                    isRolesPermittedByDefault={isRolesPermittedByDefault}
                    onChange={onChangeDefault}
                    hideTitle
                />
            </div>
            {dialogRole && (
                <RoleAccessDialog
                    role={dialogRole}
                    projectId={projectId}
                    projectName={projectName}
                    workspace={workspace}
                    currentDiffs={changes[dialogRole]}
                    folderCounts={summary?.folderCounts}
                    onSave={handleSaveDialog}
                    onClose={() => setDialogRole(null)}
                />
            )}
        </div>
    );
});

export default RoleAccessRulesSection;
